import { useMemo, useState } from 'react';

import {
  Card,
  CardSlider,
  SectionPlaceholder,
  TextLink,
  Typography,
} from 'components';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { ConditionForKit } from 'types/Conditions';
import { ConditionCard } from './ConditionCard';
import { ConditionInfoModal } from './ConditionInfoModal';

import styles from './ConditionsList.module.css';

type ConditionsListProps = {
  type: string;
  conditions: ConditionForKit[];
};

const ConditionAssociationCard = ({
  condition,
}: {
  condition: ConditionForKit;
}) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  return (
    <Card
      key={condition.id}
      title={
        <Typography variant='heading-m' className={styles.title}>
          {condition.display_title}
        </Typography>
      }
    >
      <Typography variant='label' className={styles.associationText}>
        {condition.evaluation}
      </Typography>
      <div className={styles.details}>
        <TextLink
          label='View details'
          iconNameRight='chevronForward'
          to={`/results/${currentKitId}/conditions/${condition.id}`}
        />
      </div>
    </Card>
  );
};

const conditionsOrder = {
  flag: 0,
  borderline: 1,
  clear: 2,
  neutral: 3,
};

export const sortConditionsByEvaluation = (conditions: ConditionForKit[]) => {
  if (!conditions || !conditions.length) return null;
  return conditions.sort((a, b) => {
    return (
      conditionsOrder[a.evaluation_meaning ?? a] -
      conditionsOrder[b.evaluation_meaning ?? b]
    );
  });
};
export const ConditionsList = ({ type, conditions }: ConditionsListProps) => {
  const [showModal, setShowModal] = useState(false);
  const currentKit = useCurrentKitAndTinyAccount().currentKit;
  const toggleConditionInfoModal = () => {
    setShowModal(!showModal);
  };
  const conditionsInSample = conditions.filter(
    condition => condition.kit_id !== null,
  );
  const cardsToShow = useMemo(() => {
    return type === 'Biomarkers'
      ? (sortConditionsByEvaluation(conditionsInSample)?.map(
          (condition: ConditionForKit) => (
            <ConditionCard
              kit={currentKit}
              condition={condition}
              title={condition.display_title}
              key={condition.id}
              withDetails
              withFooter
            />
          ),
        ) ?? [])
      : conditionsInSample.map(condition => (
          <ConditionAssociationCard key={condition.id} condition={condition} />
        ));
  }, [type, conditionsInSample, currentKit]);

  return (
    <>
      <ConditionInfoModal
        showModal={showModal}
        toggle={toggleConditionInfoModal}
      />
      {!conditionsInSample?.length ? (
        <SectionPlaceholder title={`No ${type?.toLowerCase()} available`}>
          <div className={styles.sectionPlaceholder}>
            <span>{`This sample doesn't have any Conditions ${type} available. `}</span>
            <span
              className={styles.greyLink}
              onClick={() => {
                toggleConditionInfoModal();
              }}
            >
              Learn more.
            </span>
          </div>
        </SectionPlaceholder>
      ) : (
        <>
          <div className={styles.conditionsGridListContainer}>
            {cardsToShow}
          </div>
          <div className={styles.conditionsSliderContainer}>
            <CardSlider cards={cardsToShow} />
          </div>
        </>
      )}
    </>
  );
};
